import { zipObject } from 'lodash';

/**
 * Local Storage utilities
 * =======================
 * Safe getters and setters for interacting with LocalStorage.
 * All stored items are key-prefixed with 'GOAT.*' for better organization.
 */

const PREFIX = 'GOAT.';
const PREFIX_REGEXP = new RegExp(`^${PREFIX}`);

/**
 * [setItem]
 * JSON stringifies the input value and stores it under the GOAT prefix.
 * e.g. setItem('foo', 'bar')  ->  { 'GOAT.foo': '"bar"' }
 * @param {String} key
 * @param {any}    value
 */
const setItem = (key: string, value: any) => {
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(`${PREFIX}${key}`, serialized);
    return value;
  } catch (error) {
    return error;
  }
};

/**
 * [getItem]
 * Retrieve the requested item. Lookups may contain the GOAT prefix, or not.
 * e.g. getItem('foo') and getItem('GOAT.foo') will both yield the value stored at 'GOAT.foo'
 * @param {String} key
 */
const getItem = (key: string) => {
  try {
    const searchKey = PREFIX_REGEXP.test(key) ? key : `${PREFIX}${key}`;
    const serialized = localStorage.getItem(searchKey);
    return JSON.parse(serialized);
  } catch {
    return null;
  }
};

/**
 * [listItems]
 * Returns an object containing all GOAT-prefixed key/value pairs.
 * @return {Object}
 */
const listItems = () => {
  try {
    const keys = Object.keys(localStorage).filter((key) => {
      return PREFIX_REGEXP.test(key);
    });
    const values = keys.map((key) => getItem(key));
    return zipObject(keys, values);
  } catch (error) {
    return error;
  }
};

export default {
  getItem,
  listItems,
  setItem,
};
